import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { useStore } from '../../../hooks/useStore';
import { useCompanyReferralLink } from '../../../queries/company';
import { Accordion, Page, RoundedPaper } from '../../../lib/components';
import { FeedbackAlert } from '../../../lib/components/atomic/feedback/alert';
import { Button } from '../../../lib/components/atomic/button';
import {
  CheckmarkIcon,
  ChristmasGiftIcon,
  CopyIcon,
  HornIcon,
  InfoIcon,
  PercentIcon,
  ShareArrowIcon,
  ShoppingBagIcon,
  SparklesIcon
} from '../../../lib/icons';

export const KundenWerbenKunden = observer(() => {
  const { t } = useTranslation('referral');
  const { companyStore } = useStore();
  const [copied, setCopied] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const { data: referralLinkData } = useCompanyReferralLink(companyStore.company?.id ?? '');
  const referralLink = referralLinkData?.url ?? '';
  const [linkPrefix, campaignSlug] = referralLink.split(/(?<=utm_campaign=)/);

  const rewardOptions = [
    {
      icon: <ShoppingBagIcon width={40} height={40} className="text-secondary-main" />,
      title: t('REWARD_1_TITLE'),
      body: t('REWARD_1_BODY')
    },
    {
      icon: <ChristmasGiftIcon width={40} height={40} className="text-secondary-main" />,
      title: t('REWARD_2_TITLE'),
      body: t('REWARD_2_BODY')
    },
    {
      icon: <SparklesIcon width={40} height={40} className="text-secondary-main" />,
      title: t('REWARD_3_TITLE'),
      body: t('REWARD_3_BODY')
    }
  ];

  const handleCopy = () => {
    const fallback = () => {
      const ta = document.createElement('textarea');
      ta.value = referralLink;
      ta.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch (_) {
        enqueueSnackbar(t('COPY_ERROR'), { variant: 'error' });
      }
      ta.remove();
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(referralLink).catch(fallback);
    } else {
      fallback();
    }

    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    enqueueSnackbar(t('COPY_SUCCESS'), { variant: 'success' });
  };

  return (
    <Page>
      <div className="flex flex-col gap-6 pb-8">
        {/* Hero card */}
        <RoundedPaper className="!bg-heaven-600 text-white p-8">
          <div>
            <div className="flex items-center gap-4 mb-4">
              <HornIcon width={40} height={40} className="text-white" />
              <div className="text-h2 font-medium">{t('HERO_TITLE')}</div>
            </div>

            <div className="text-white">{t('HERO_LEDE')}</div>

            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="flex items-center gap-4 p-3 rounded-xl bg-heaven-500">
                <PercentIcon width={36} height={36} className="text-white ml-1" />
                <div className="flex flex-col">
                  <span className="font-medium text-white">{t('TILE_LEFT_TAG')}</span>
                  <span className="text-base font-medium">{t('TILE_LEFT_VALUE')}</span>
                </div>
              </div>

              <div className="flex items-center gap-4 rounded-xl p-3 bg-heaven-500">
                <ChristmasGiftIcon width={36} height={36} className="text-white ml-1" />
                <div className="flex flex-col">
                  <span className="font-medium text-white">{t('TILE_RIGHT_TAG')}</span>
                  <span className="text-base font-medium">{t('TILE_RIGHT_VALUE')}</span>
                </div>
              </div>
            </div>
          </div>
        </RoundedPaper>

        <div className="flex flex-row gap-6 w-full">
          {/* Link card */}
          <RoundedPaper className="w-2/3 p-6 flex flex-col gap-4 justify-between">
            <div className="flex gap-4">
              <span className="inline-flex items-center justify-center rounded-lg flex-shrink-0 bg-heaven-100 w-[38px] h-[38px]">
                <ShareArrowIcon width={20} height={20} className="text-primary-main" />
              </span>
              <div className="flex flex-col gap-1">
                <div className="font-medium text-primary-main">{t('LINK_TITLE')}</div>
                <div className="text-sm text-grey-600">{t('LINK_SUBTITLE')}</div>
              </div>
            </div>

            <div className="flex gap-4">
              <div
                className="bg-grey-50 border border-grey-200 rounded-lg px-4 py-3 text-primary-main overflow-hidden whitespace-nowrap text-ellipsis select-all"
                title={referralLink}
              >
                <span className="text-grey-500">{linkPrefix}</span>
                <span className="text-heaven-700">{campaignSlug}</span>
              </div>
              <Button
                disabled={copied || !referralLink}
                className="w-1/5"
                leadingIcon={
                  copied ? (
                    <CheckmarkIcon width={16} height={16} />
                  ) : (
                    <CopyIcon width={16} height={16} />
                  )
                }
                onClick={handleCopy}
              >
                <div className="min-w-20">{copied ? t('COPIED') : t('COPY')}</div>
              </Button>
            </div>

            <FeedbackAlert severity="warning" description={t('WARNING')} />
          </RoundedPaper>

          {/* How it works */}
          <RoundedPaper className="w-1/3 p-6">
            <div className="flex items-center gap-3 mb-4">
              <InfoIcon className="border-full border-primary-main text-primary-main h-8 w-8" />
              <div className="font-medium text-primary-main">{t('HOW_TITLE')}</div>
            </div>
            <ol className="list-none m-0 ml-1 p-0 flex flex-col gap-3">
              {[t('STEP_1'), t('STEP_2'), t('STEP_3'), t('STEP_4')].map((step, i) => (
                <li key={step} className="flex items-center gap-4">
                  <span className="inline-flex items-center justify-center rounded-full flex-shrink-0 bg-heaven-100 text-heaven-700 border border-heaven-200 font-medium w-6 h-6">
                    {i + 1}
                  </span>
                  <span className="text-base text-primary-main leading-snug">{step}</span>
                </li>
              ))}
            </ol>
          </RoundedPaper>
        </div>

        <div className="flex flex-row gap-6 w-full">
          {/* Rewards */}
          <RoundedPaper className="w-2/3 p-6 h-fit">
            <div className="font-medium text-primary-main mb-6">{t('REWARDS_TITLE')}</div>
            <div className="flex flex-col gap-6">
              {rewardOptions.map((option) => (
                <div key={option.title} className="flex gap-4 items-center">
                  <span className="inline-flex items-center justify-center w-12 h-12">
                    {option.icon}
                  </span>
                  <div className="flex flex-col gap-1">
                    <div className="font-medium text-primary-main">{option.title}</div>
                    <div className="text-sm text-grey-700">{option.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </RoundedPaper>

          {/* FAQ */}
          <RoundedPaper className="w-1/3 p-6 h-fit" style={{ flexGrow: 1 }}>
            <div className="font-medium text-primary-main mb-2">{t('FAQ_TITLE')}</div>
            {[
              { q: t('FAQ_1_Q'), a: t('FAQ_1_A') },
              { q: t('FAQ_2_Q'), a: t('FAQ_2_A') },
              { q: t('FAQ_3_Q'), a: t('FAQ_3_A') },
              { q: t('FAQ_4_Q'), a: t('FAQ_4_A') }
            ].map((item, i) => (
              <Accordion
                key={item.q}
                headlineData={{ mainText: item.q }}
                expanded={openFaq === i}
                onChange={(_, isExpanded) => setOpenFaq(isExpanded ? i : null)}
              >
                <div className="text-sm text-grey-700 text-left mb-4">{item.a}</div>
              </Accordion>
            ))}
          </RoundedPaper>
        </div>
      </div>
    </Page>
  );
});
